// ─── SellerFlow Theme System ──────────────────────────────────────────────────
// Dark mode: Wealth Square palette (deep navy, violet accents, glass cards)
// Light mode: Lavender-grey palette (soft purple-tinted whites)

export const THEMES = {
  dark: {
    // ── Backgrounds ──────────────────────────────────────────────────────────
    // Daytime-friendly palette: the main page bg is bumped up a notch
    // from the original near-black so it's easier on the eyes in bright
    // ambient light. Surfaces stay at the original dark navy so cards
    // visually recede against the lighter page canvas (user feedback —
    // contrast looks right once the bg is lifted and surfaces stay put).
    bg:          "#1f2444",        // navy — daytime-friendly; lifted again from #161a30
    bgSecondary: "#171b36",        // sidebar (slightly darker than bg)
    surface:     "#131629",        // original card surface (kept — strong contrast vs new bg)
    surfaceHi:   "#1a1e35",        // original elevated card (kept)
    card:        "#131629",
    cardHi:      "#1e2240",

    // ── Borders ──────────────────────────────────────────────────────────────
    border:      "rgba(255,255,255,0.08)",
    borderHi:    "rgba(139,92,246,0.32)",  // violet glow border

    // ── Text ─────────────────────────────────────────────────────────────────
    // Pure white for primary text per user request — headings, KPI
    // values, card titles, table rows. Secondary tiers (textSub /
    // textMuted) stay dimmer for hierarchy.
    text:        "#ffffff",
    textSub:     "#c4c8de",        // muted blue-grey — slightly brighter
    textMuted:   "#7a7e9a",        // bumped from #636780 for daylight legibility

    // ── Accent — violet/purple primary (Wealth Square) ───────────────────────
    blue:        "#8b5cf6",        // violet primary
    blueHi:      "#a78bfa",
    accent:      "#8b5cf6",
    accentHi:    "#a78bfa",
    accentGlow:  "rgba(139,92,246,0.25)",

    // ── Semantic colors ───────────────────────────────────────────────────────
    green:       "#10b981",
    greenHi:     "#34d399",
    red:         "#ef4444",
    redHi:       "#f87171",
    orange:      "#f59e0b",
    orangeHi:    "#fbbf24",
    purple:      "#8b5cf6",
    purpleHi:    "#a78bfa",
    teal:        "#06b6d4",
    tealHi:      "#22d3ee",
    yellow:      "#eab308",
    yellowHi:    "#facc15",
    pink:        "#ec4899",
    pinkHi:      "#f472b6",

    // ── Semantic aliases ──────────────────────────────────────────────────────
    income:      "#10b981",
    expense:     "#ef4444",
    neutral:     "#8b8fa8",

    // ── Glassmorphism ─────────────────────────────────────────────────────────
    glass:       "rgba(19,22,41,0.7)",
    glassBorder: "rgba(255,255,255,0.06)",
    blur:        "blur(24px)",
    headerBg:    "rgba(13,15,26,0.85)",

    // ── Shadows ───────────────────────────────────────────────────────────────
    shadow:      "0 4px 24px rgba(0,0,0,0.5)",
    shadowLg:    "0 12px 48px rgba(0,0,0,0.7)",
    shadowXl:    "0 32px 80px rgba(0,0,0,0.9)",
    shadowViolet:"0 8px 32px rgba(139,92,246,0.25)",

    // ── Gradient glow (for chart bg, hero areas) ──────────────────────────────
    glowViolet:  "radial-gradient(ellipse at 60% 40%, rgba(139,92,246,0.15) 0%, transparent 60%)",
    glowBlue:    "radial-gradient(ellipse at 30% 70%, rgba(6,182,212,0.10) 0%, transparent 60%)",

    // ── Tooltip ───────────────────────────────────────────────────────────────
    tooltipBg:   "#1a1e35",
    tooltipText: "#f0f2ff",
  },

  light: {
    // ── Backgrounds — warm light gray (like Eonix reference) ────────────────
    bg:          "#e9e7e4",        // warm light gray page background
    bgSecondary: "#e0deda",
    surface:     "#f4f3f1",        // light warm surface for inputs/sections
    surfaceHi:   "#eceae7",        // slightly elevated warm surface
    card:        "#ffffff",        // clean white cards (same as reference)
    cardHi:      "#f8f7f5",

    // ── Borders ──────────────────────────────────────────────────────────────
    border:      "rgba(0,0,0,0.09)",
    borderHi:    "rgba(0,0,0,0.18)",

    // ── Text — near black & readable grays ───────────────────────────────────
    text:        "#111111",        // near-black headings
    textSub:     "#5c6070",        // medium gray
    textMuted:   "#9ba3b5",

    // ── Accent ───────────────────────────────────────────────────────────────
    blue:        "#6c5ce7",
    blueHi:      "#8b5cf6",
    accent:      "#6c5ce7",
    accentHi:    "#8b5cf6",
    accentGlow:  "rgba(108,92,231,0.12)",

    // ── Semantic ─────────────────────────────────────────────────────────────
    green:       "#00a86b",
    greenHi:     "#10b981",
    red:         "#e8304e",
    redHi:       "#ef4444",
    orange:      "#e67e22",
    orangeHi:    "#f39c12",
    purple:      "#6c5ce7",
    purpleHi:    "#8b5cf6",
    teal:        "#0891b2",
    tealHi:      "#06b6d4",
    yellow:      "#d4ac0d",
    yellowHi:    "#f1c40f",
    pink:        "#e84393",
    pinkHi:      "#ec4899",

    // ── Semantic aliases ───────────────────────────────────────────────────────
    income:      "#00a86b",
    expense:     "#e8304e",
    neutral:     "#555b6e",

    // ── Glass ─────────────────────────────────────────────────────────────────
    glass:       "rgba(255,255,255,0.85)",
    glassBorder: "rgba(0,0,0,0.07)",
    blur:        "blur(16px)",
    headerBg:    "rgba(255,255,255,0.92)",

    // ── Shadows — clean, minimal ───────────────────────────────────────────────
    shadow:      "0 1px 4px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.05)",
    shadowLg:    "0 4px 20px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
    shadowXl:    "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)",
    shadowViolet:"0 8px 32px rgba(108,92,231,0.18)",

    // ── Glow ─────────────────────────────────────────────────────────────────
    glowViolet:  "radial-gradient(ellipse at 60% 40%, rgba(108,92,231,0.06) 0%, transparent 60%)",
    glowBlue:    "radial-gradient(ellipse at 30% 70%, rgba(8,145,178,0.04) 0%, transparent 60%)",

    // ── Tooltip ───────────────────────────────────────────────────────────────
    tooltipBg:   "#1a1a1a",
    tooltipText: "#ffffff",
  }
};

// ── Mode detection ─────────────────────────────────────────────────────────────
function getInitialMode() {
  try {
    const saved = localStorage.getItem("sf_theme_mode");
    if (saved) return saved;
  } catch {}
  return "dark";
}

function getAutoTheme() {
  const hour = new Date().getHours();
  return (hour >= 20 || hour < 8) ? "dark" : "light";
}

export function resolveTheme(mode) {
  if (mode === "auto") return THEMES[getAutoTheme()];
  return THEMES[mode] || THEMES.dark;
}

export { getInitialMode, getAutoTheme };

// ── Typography ─────────────────────────────────────────────────────────────────
export const TYPE = {
  hero:    { fontSize: 48, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1.1 },
  h1:      { fontSize: 32, fontWeight: 700, letterSpacing: -0.8, lineHeight: 1.15 },
  h2:      { fontSize: 24, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.2 },
  h3:      { fontSize: 19, fontWeight: 600, letterSpacing: -0.3, lineHeight: 1.3 },
  body:    { fontSize: 17, fontWeight: 400, letterSpacing: -0.1, lineHeight: 1.6 },
  bodyMd:  { fontSize: 15, fontWeight: 400, letterSpacing: 0,    lineHeight: 1.5 },
  bodySm:  { fontSize: 13, fontWeight: 400, letterSpacing: 0,    lineHeight: 1.45 },
  label:   { fontSize: 11, fontWeight: 700, letterSpacing: 0.6,  lineHeight: 1.3, textTransform: "uppercase" },
  caption: { fontSize: 11, fontWeight: 400, letterSpacing: 0,    lineHeight: 1.3 },
};

// ── Radius ─────────────────────────────────────────────────────────────────────
export const RADIUS = {
  xs:   4,
  sm:   8,
  md:   12,
  lg:   18,
  xl:   24,
  xxl:  32,
  pill: 980,
};

// ── Easing ─────────────────────────────────────────────────────────────────────
export const SPRING   = "cubic-bezier(0.34,1.3,0.64,1)";
export const EASE_OUT = "cubic-bezier(0.25,0.46,0.45,0.94)";
export const EASE_IN  = "cubic-bezier(0.55,0,1,0.45)";

// ── Spacing ────────────────────────────────────────────────────────────────────
export const SPACE = { 1:4, 2:8, 3:12, 4:16, 5:20, 6:24, 8:32, 10:40, 12:48, 16:64 };

// ── Font ───────────────────────────────────────────────────────────────────────
export const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// ── Order colors ───────────────────────────────────────────────────────────────
export const ORDER_COLORS = [
  { bg: "rgba(139,92,246,0.12)",  border: "#8b5cf6", text: "#a78bfa" },
  { bg: "rgba(6,182,212,0.12)",   border: "#06b6d4", text: "#22d3ee" },
  { bg: "rgba(16,185,129,0.12)",  border: "#10b981", text: "#34d399" },
  { bg: "rgba(245,158,11,0.12)",  border: "#f59e0b", text: "#fbbf24" },
  { bg: "rgba(239,68,68,0.12)",   border: "#ef4444", text: "#f87171" },
  { bg: "rgba(236,72,153,0.12)",  border: "#ec4899", text: "#f472b6" },
  { bg: "rgba(234,179,8,0.12)",   border: "#eab308", text: "#facc15" },
  { bg: "rgba(99,102,241,0.12)",  border: "#6366f1", text: "#818cf8" },
];
